import { faPaintbrush } from "@fortawesome/pro-regular-svg-icons/faPaintbrush";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Dialog, DialogActions, DialogTitle, Grid, IconButton, TextField, Typography } from '@mui/material';
import { FileThumbnaildGetDto, RequestLifeCycle } from "app/appModels";
import { MprcButton, MprcRequestErrorDialog, MprcRequestErrorReport, MprcRequestIndicator, MprcText } from "components";
import { ConvertUploadToFileThumbnaildGetDto, UploadingStatus } from "components/FileUpload/AttachSelection";
import { IconButtonClose } from "components/iconButtons";
import { IconButtonExpandScreen } from "components/iconButtons/IconButtonExpandScreen";
import { Delta, EmitterSource, QuillCommands, QuillUnprivilegedEditor } from "components/Quill";
import {
    AsyncUploadResult,
    GetDevPlanActivityPublicationDto,
    GetPublicationAttachDto,
    PublicationAttachFileItem,
    PublicationAttachType,
    PublicationCreationSettingsResponse,
    PublicationType
} from "generated/CoreApi_v1";
import { useReferredState } from "hooks/useReferredState";
import LibUtils from "lib/LibUtils";
import LinkifyIt from "linkify-it";
import { useSnackbar } from "notistack";
import { useCoreApi } from "providers/CoreApiProvider";
import * as React from 'react';
import { useEffect, useRef, useState } from "react";
import { useTranslation } from 'react-i18next';
import { NIL, v4 as uuidv4 } from 'uuid';
import {
    isPresentationAttach, PublicationAttachFileHandler,
    PublicationAttachView,
    PublicationEditor,
    PublicationMediaView, PublicationMediaViewData
} from "views/knowledge/publication/creation/components";
import { PublicationDialogContent } from "views/knowledge/publication/creation/components/PublicationDialogContent";
import { forceRichEditor } from "views/knowledge/publication/creation/CreatePost";

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

interface IValidation {
    title?: boolean;
    description?: boolean;
}

interface IState {
    record: GetDevPlanActivityPublicationDto;
    validation: IValidation;
    uploadingStatus: UploadingStatus;
    forceRichEditor: boolean,
}

export interface CreateActivityProps {
    devPlanGroupId: string;
    publicationId?: string;
    readOnly?: boolean;
    onClose: (success: boolean | null) => void;
}

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

export const CreateDevPlanActivity: React.FC<CreateActivityProps> = (props) => {
    const { devPlanGroupId, readOnly = false, onClose } = props;

    const qlEditorId = `ql${new Date().getTime()}`;
    const minHeight = 200;

    const api = useCoreApi();
    const { t } = useTranslation("lms");
    const { enqueueSnackbar } = useSnackbar();

    const [step] = useState<number>(1);
    const [lastError, setLastError] = useState<any>();
    const [formCycle, setFormCycle] = useState<RequestLifeCycle>(RequestLifeCycle.None);
    const [fullScreenMode, setFullScreenMode] = useState<boolean>(false);
    const [richEditor, setRichEditor] = useState<boolean>(false);
    const [loadDto, setLoadDto] = useState<GetDevPlanActivityPublicationDto>();
    const [recordChanged, setRecordChanged] = useState<boolean>(false);

    const metadataProcessing = useRef<string[]>([]);
    const initialContentRef = useRef<string>("");
    const editorRef = useRef<QuillCommands | null>(null);
    const config = useRef<PublicationCreationSettingsResponse | null>({ allowAttachVideo: true, maxAttachSize: null, allowInstitutionalPublisher: false, channelSuggestions: [], defaultChannel: [], topics: [] });

    const steps = [];

    const [state, setState, refState] = useReferredState<IState>(
        {
            record: {
                publicationId: NIL,
                title: "",
                content: "",
                attachs: [],
                publicationType: PublicationType.DevPlanActivity,
                presentation: null,
            },
            validation: {
                title: undefined,
                description: undefined,
            },
            uploadingStatus: "none",
            forceRichEditor: false,
        });


    ///
    const updateRecord = (data: Partial<GetDevPlanActivityPublicationDto>) => {
        return { ...refState.current.record, ...data };
    };

    ///
    const validateDescription = (text: string, html: string, files: GetPublicationAttachDto[] | null) => {
        return (html != null && html != "<p><br></p>") || (files != null && files.length > 0);
    };

    ///
    const handleEditorChange = (content: string, delta: Delta, source: EmitterSource, editor: QuillUnprivilegedEditor) => {
        const text = editor.getText().trim();
        const html = editor.getHTML();

        setState({
            ...refState.current,
            record: updateRecord({ content: html }),
            validation: { ...refState.current.validation, description: validateDescription(text, html, state.record.attachs) }
        });

        analyse(content);
    };

    ///
    const handlePresentation = (data: PublicationMediaViewData) => {
        setState({
            ...state,
            record: updateRecord({ presentation: data.presentation, attachs: data.attachs })
        });
    };

    ///
    const handleFileUploaded = (e: AsyncUploadResult) => {
        const data: FileThumbnaildGetDto = ConvertUploadToFileThumbnaildGetDto(e);
        const attachType: PublicationAttachType = (PublicationAttachType[e.mainFile.metadata.type as any]) as any;
        const attachs = refState.current.record.attachs.splice(0);

        var at = {
            attachId: uuidv4(),
            file: {
                fileId: data.mainFile.fileId,
                metadata: data.mainFile.metadata,
                mimetype: data.mainFile.mimetype,
                url: data.mainFile.url
            },
            thumbnail: data.thumbnail == null
                ? null
                : {
                    fileId: data.thumbnail.fileId,
                    metadata: data.thumbnail.metadata,
                    mimetype: data.thumbnail.mimetype,
                    url: data.thumbnail.url
                },
            attachType: attachType,
            isEmbed: false,
            linkMetadata: null,
            videoProcessed: false,
            caption: null,
        } as GetPublicationAttachDto;

        attachs.push(at);

        const presentation = addToPresentation(at);

        setState({
            ...refState.current,
            record: updateRecord({ attachs: attachs, presentation: presentation })
        });
    };

    ///
    const handleUploadingStatus = (status: UploadingStatus) => {
        setState({ ...refState.current, uploadingStatus: status });
    };

    ///
    const handleTitleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        var v = event.target.value;
        setState({
            ...state,
            record: updateRecord({ title: v }),
            validation: { ...state.validation, title: v.length > 0 }
        });
    };

    ///
    const analyse = (text: string) => {
        const attachs = refState.current.record.attachs.slice(0) ?? [];
        const linkify = new LinkifyIt();
        const matches = linkify.match(text);

        if (matches == null || matches.length == 0)
            return;

        matches.forEach((m) => {
            const existsRequest = metadataProcessing.current.some(x => x == m.url);
            if (!existsRequest && attachs.find(p => p.linkMetadata != null && p.linkMetadata.url == m.url) == null) {
                metadataProcessing.current.push(m.url);
                api.v1.urlParser.getMetadata({ url: m.url })
                    .then((response) => {
                        if (response.status == 200) {
                            const metadata = response.data;
                            if (metadata.externalId == null || attachs.find(p => p.linkMetadata != null && p.linkMetadata.externalId == metadata.externalId) == null) {
                                const attachType: PublicationAttachType = (PublicationAttachType[metadata.provider as any]) as any;

                                var at = {
                                    attachId: uuidv4(),
                                    file: null,
                                    thumbnail: null,
                                    attachType: attachType,
                                    isEmbed: false,
                                    linkMetadata: metadata,
                                    videoProcessed: false,
                                    caption: null,
                                } as GetPublicationAttachDto;
                                attachs.push(at);

                                const presentation = addToPresentation(at);

                                setState({
                                    ...refState.current,
                                    record: updateRecord({ attachs: attachs, presentation: presentation })
                                });
                            }
                        }
                        else {
                            enqueueSnackbar(t("knowledge:Publication.Common.NotIdentifyTypeMessage"), { variant: "error" });
                        }
                    })
                    .catch(() => {
                        enqueueSnackbar(t("knowledge:Publication.Common.NotIdentifyTypeMessage"), { variant: "error" });
                    })
                    .finally(() => {
                        metadataProcessing.current = metadataProcessing.current.filter(x => x != m.url);
                    });
            }
        });
    };

    ///
    const addToPresentation = (attach: GetPublicationAttachDto) => {
        let presentation = refState.current.record.presentation;

        if (isPresentationAttach(attach.attachType)) {
            if (!presentation) {
                presentation = { orderedItems: [attach.attachId], rotationInterval: 0 };
            }
            else {
                presentation = { ...presentation, orderedItems: [...presentation.orderedItems, attach.attachId] };
            }
        }
        return presentation;
    };

    ///
    const load = async (publicationId: string) => {
        setFormCycle(RequestLifeCycle.Loading);

        api.v1.devPlan.getDevPlanActivity({ publicationId })
            .then((result) => {
                const record = result.data;

                setState({
                    uploadingStatus: "none",
                    forceRichEditor: forceRichEditor(record.content),
                    record,
                    validation: {
                        title: true,
                        description: true,
                    },
                });

                setLoadDto(LibUtils.deepCopy(record));
                initialContentRef.current = record.content ?? "";

                setFormCycle(RequestLifeCycle.None);
            })
            .catch((error: any) => {
                setLastError(error);
                setFormCycle(RequestLifeCycle.LoadFailed);
            });
    };

    ///
    const save = () => {
        const { record } = state;

        const files: PublicationAttachFileItem[] = record.attachs.map((v) => ({
            attachId: v.attachId,
            fileId: v.file?.fileId ?? null,
            thumbnailId: v.thumbnail?.fileId ?? null,
            cardImageId: v.cardImage?.fileId ?? null,
            linkMetadata: v.linkMetadata,
            caption: v.caption
        }));

        setFormCycle(RequestLifeCycle.Posting);

        api.v1.devPlan.createOtherActivity({
            publicationId: record.publicationId,
            title: record.title,
            content: record.content,
            attachs: files,
            presentation: record.presentation,
            contentUnformatted: null,
            threadId: null,
            devPlanGroupId: devPlanGroupId,
        })
            .then((response) => {
                onClose(response.data.success);
                setFormCycle(RequestLifeCycle.None);
                enqueueSnackbar(record.publicationId == NIL ? t("DevPlan.Activity.PublishMessage") : t("DevPlan.Activity.PublishUpdateMessage"), { variant: "success" });
            })
            .catch((error) => {
                setLastError(error);
                setFormCycle(RequestLifeCycle.PostFailed);
            });
    };

    ///
    const canSave = (): boolean => {
        const { validation, uploadingStatus, record: { attachs } } = state;
        return (uploadingStatus != "uploading" && validation.title === true
            && (validation.description === true || attachs.length > 0))
            && recordChanged;
    };

    ///
    const getSaveButton = () => {
        return (
            <MprcButton
                variant="contained"
                color="success"
                onClick={save}
                disabled={!canSave()}
            >
                <span>{t("DevPlan.Activity.SaveButtonText")}</span>
            </MprcButton>
        );
    };

    ///////////////////////////////////////////////////////////////////////
    /// Passos do Formulário
    ///////////////////////////////////////////////////////////////////////

    ///
    steps[1] = () => {
        const { record, validation, forceRichEditor } = state;

        return (
            <>
                <PublicationDialogContent>

                    <TextField
                        autoFocus
                        fullWidth
                        name="title"
                        margin="normal"
                        placeholder={t("DevPlan.Activity.TitlePlaceholder")}
                        autoComplete="off"
                        tabIndex={0}
                        value={record.title}
                        error={validation.title === false}
                        slotProps={{
                            input: {
                                inputProps: { maxLength: 255 },
                                readOnly
                            }
                        }}
                        onChange={(e) => handleTitleChange(e)}
                    />

                    <PublicationEditor
                        id={qlEditorId}
                        minHeight={minHeight}
                        toolbar={richEditor || forceRichEditor}
                        initialValue={initialContentRef.current}
                        readOnly={readOnly}
                        placeholder={t("DevPlan.Activity.EditorPlaceholder")}
                        onChange={handleEditorChange}
                        ref={editorRef}
                    />

                    <PublicationMediaView
                        data={
                            {
                                presentation: record.presentation,
                                attachs: record.attachs,
                            }
                        }
                        onChange={handlePresentation}
                        onUploading={(uploading) => handleUploadingStatus(uploading ? "uploading" : "none")}
                    />

                    <PublicationAttachView
                        attachs={record.attachs}
                        onChange={
                            (attachs) => setState({
                                ...state,
                                record: updateRecord({ attachs }),
                                validation: { ...state.validation, ...validation }
                            })
                        }
                    />

                    <PublicationAttachFileHandler
                        publicationId={record.publicationId}
                        attachs={record.attachs}
                        maxSize={config.current?.maxAttachSize ?? undefined}
                        readOnly={readOnly}
                        allowAttachVideo
                        onUploadComplete={handleFileUploaded}
                        onUploadStatusChange={handleUploadingStatus}
                        additionalLeftButton={
                            [
                                <IconButton key="richEditor" onClick={() => setRichEditor(!richEditor)}>
                                    <MprcText color={richEditor ? "highlight" : "inherit"}>
                                        <FontAwesomeIcon icon={faPaintbrush} fixedWidth />
                                    </MprcText>
                                </IconButton>
                            ]
                        }
                    />
                </PublicationDialogContent>

                <DialogActions>
                    <Grid container justifyContent="end" alignItems="center" width="100%">
                        <Grid>
                            {
                                getSaveButton()
                            }
                        </Grid>
                    </Grid>
                </DialogActions>
            </>
        );
    };

    ///////////////////////////////////////////////////////////////////////
    /// Effect's
    ///////////////////////////////////////////////////////////////////////

    useEffect(() => {
        if (richEditor) editorRef.current?.focus();
    }, [richEditor]);

    useEffect(() => {
        const isRecordChanged = !LibUtils.deepCompare(state.record, loadDto);
        setRecordChanged(isRecordChanged);
    }, [state]);

    // Ao carregar registro para edição
    useEffect(() => {
        if (props.publicationId)
            load(props.publicationId);
    }, []);

    ///////////////////////////////////////////////////////////////////////
    /// Render
    ///////////////////////////////////////////////////////////////////////
    return (
        <Dialog
            open={true}
            maxWidth={"md"}
            fullWidth
            fullScreen={fullScreenMode}
            sx={fullScreenMode ? { "& .MuiPaper-root": { maxHeight: "90%", maxWidth: "90%" } } : undefined}
        >
            <DialogTitle>
                <Grid container justifyContent="space-between">
                    <Grid>
                        <Typography variant="h4">
                            {
                                t(
                                    state.record.publicationId == NIL
                                        ? "DevPlan.Activity.TitleNew"
                                        : "DevPlan.Activity.TitleEdit"
                                )
                            }
                        </Typography>
                    </Grid>
                    <Grid>
                        <IconButtonExpandScreen
                            expanded={fullScreenMode}
                            size="small"
                            onClick={() => setFullScreenMode(!fullScreenMode)}
                        />
                        <IconButtonClose
                            color="inherit"
                            size="small"
                            onClick={() => onClose(null)}
                        />
                    </Grid>
                </Grid>
            </DialogTitle>

            <MprcRequestIndicator type="request" visible={formCycle == RequestLifeCycle.Loading} />

            <MprcRequestIndicator type="post" visible={formCycle == RequestLifeCycle.Posting} />

            {
                lastError && formCycle == RequestLifeCycle.LoadFailed
                    ? <MprcRequestErrorReport error={lastError} />
                    : (
                        <>
                            <MprcRequestErrorDialog error={lastError} onClose={() => setLastError(null)} />
                            {steps[step]()}
                        </>
                    )
            }

        </Dialog>
    );
};
